import type { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import type {
  IsLiteral,
  LiteralToPrimitive,
  NonEmptyTuple,
  Primitive,
  ValueOf,
} from 'type-fest';

export type FalsyLiteral = '' | 0 | false | null | undefined;
export type NonFalsy<T> = Exclude<T, FalsyLiteral>;

type WidenLiteral<T> = true extends IsLiteral<T> ? LiteralToPrimitive<T> : T;
export type TemplateRecord<T extends object> = Readonly<T> &
  ([keyof T] extends [string]
    ? Partial<{ [key: string]: WidenLiteral<ValueOf<T>> | undefined }>
    : object);

/**
 * Resolve a `Record` of {@linkcode FormControl} representing the given flat interface
 */
export type FormControls<FormValues> = {
  [K in keyof FormValues]-?: FormControl<
    { [K_ in K]?: FormValues[K] } extends Pick<FormValues, K>
      ? FormValues[K] | undefined
      : FormValues[K]
  >;
};

/**
 * Resolve a {@linkcode FormGroup} representing the given flat interface
 */
export type FormGroupOfValues<T> = FormGroup<FormControls<T>>;

/**
 * Extract the value represented by {@linkcode FormGroup}
 * with the provided {@linkcode FormControls}
 */
export type FormValues<
  FormControls extends {
    [K in keyof FormControls]: AbstractControl;
  },
> = FormGroup<FormControls>['value'];

/**
 * Extract the raw value represented by {@linkcode FormGroup}
 * with the provided {@linkcode FormControls}
 */
export type RawFormValues<
  FormControls extends {
    [K in keyof FormControls]: AbstractControl;
  },
> = ReturnType<FormGroup<FormControls>['getRawValue']>;

export type EmptyObject<T> = { [K in keyof T]?: never };
type EmptyObjectOf<T> = EmptyObject<T> extends T ? EmptyObject<T> : never;

export interface TypeguardSet<T> extends ReadonlySet<T> {
  has(searchElement: unknown): searchElement is T;
}
export const typeguardSet = <const T extends Primitive>(
  items: NonEmptyTuple<T>,
) => new Set(items) as Set<T> & { has: never } as TypeguardSet<T>;

export interface TypeguardTuple<T> extends Omit<readonly [T, ...T[]], never> {
  includes(searchElement: unknown): searchElement is T;
  includes(searchElement: T, fromIndex?: number): boolean;
}
export const typeguardTuple = <const T extends Primitive>(
  tuple: readonly [T, ...T[]],
) => tuple as Omit<readonly [T, ...T[]], never> as TypeguardTuple<T>;

// This module augmentation can be removed if the DefinitelyTyped PR is merged
// https://github.com/DefinitelyTyped/DefinitelyTyped/pull/60401
declare module 'lodash' {
  interface LoDashStatic {
    /**
     * Checks if value is empty. A value is considered empty unless it’s an arguments object, array, string, or
     * jQuery-like collection with a length greater than 0 or an object with own enumerable properties.
     *
     * @param value The value to inspect.
     * @return Returns true if value is empty, else false.
     */
    isEmpty<T extends { __trapAny: any }>(value?: T): boolean;
    isEmpty(value: string | null | undefined): value is '' | null | undefined;
    isEmpty(value: any[] | null | undefined): boolean;
    isEmpty(
      value: ReadonlyArray<any> | null | undefined,
    ): value is Readonly<[]> | null | undefined;
    isEmpty(
      // eslint-disable-next-line @typescript-eslint/unified-signatures
      value:
        | Map<any, any>
        | Set<any>
        | import('lodash').List<any>
        | null
        | undefined,
    ): boolean;
    isEmpty<T extends object>(
      value: T | null | undefined,
    ): value is EmptyObjectOf<T> | null | undefined;
    isEmpty(value?: any): boolean;
  }
}
